import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import FAQAccordion from './FAQAccordion';
import Seo from '../Seo';

const heroImage = '/award-logos/cd11.png';
const secondaryImage = '/award-logos/cd12.png';
const fallbackImage = '/services_detail_tech_1773741342619.png';

const capabilities = [
  { title: 'Cloud-Native Architecture & Migration', desc: 'Accelerate your transition to AWS, Azure, or GCP using automated landing zones, well-architected reviews, and multi-cloud governance frameworks.' },
  { title: 'Continuous Delivery (CI/CD) Automation', desc: 'Eradicate manual release bottlenecks with automated build, test, and deployment pipelines that ensure high-velocity software delivery with zero-loss quality.' },
  { title: 'Infrastructure as Code (IaC)', desc: 'Eliminate environment drift and human error by managing your entire global infrastructure through version-controlled Terraform, Ansible, and Crossplane definitions.' },
  { title: 'DevSecOps & Automated Compliance', desc: 'Shift security left by integrating automated vulnerability scanning, secret management, and compliance-as-code into your delivery lifecycle.' },
  { title: 'Containerization & Orchestration', desc: 'Modernize legacy monoliths into scalable microservices using Kubernetes, Docker, and service-mesh architectures for ultimate operational elasticity.' },
  { title: 'Site Reliability Engineering (SRE)', desc: 'Improve system availability and resilience through automated monitoring, SLI/SLO tracking, and self-healing incident response logic.' }
];



const modules = [
  { title: 'Cloud Strategy & Advisory', points: ['Migration readiness (6 Rs)', 'Multi-cloud governance', 'Cost-optimization roadmap'] },
  { title: 'Platform Engineering', points: ['Internal Developer Platforms', 'Landing Zone acceleration', 'FinOps & Spend visibility'] },
  { title: 'DevSecOps Integration', points: ['Automated SAST/DAST scans', 'Container image hardening', 'Secret & Key management'] },
  { title: 'Managed Cloud Ops', points: ['24/7 SRE support', 'Patch & Release management', 'Disaster recovery automation'] }
];

const executionSteps = [
  { phase: 'Assessment & Strategy', desc: 'Evaluate existing technical debt, workload dependencies, and cloud readiness to define a risk-mitigated migration pathway.' },
  { phase: 'Architecture & Governance', desc: 'Design secure landing zones and IAM frameworks that align with global compliance and security standards.' },
  { phase: 'Automation & Build', desc: 'Develop Infrastructure as Code (IaC) and CI/CD pipelines to create a repeatable, auditable deployment foundation.' },
  { phase: 'Workload Migration', desc: 'Execute phased migrations using lift-and-shift, re-platforming, or refactoring strategies based on workload priority.' },
  { phase: 'Optimization & Security', desc: 'Implement FinOps for cost control and DevSecOps for integrated security, ensuring the environment remains performant and safe.' },
  { phase: 'SRE & Continuous Improvement', desc: 'Monitor system health through SLIs/SLOs and refine automation to improve reliability and reduce operational toil.' }
];

const techStack = [
  { title: 'Cloud Platforms', items: ['AWS', 'Microsoft Azure', 'Google Cloud (GCP)'] },
  { title: 'Orchestration', items: ['Kubernetes (EKS/AKS/GKE)', 'Docker', 'OpenShift'] },
  { title: 'Automation / IaC', items: ['Terraform', 'Ansible', 'Pulumi', 'CloudFormation'] },
  { title: 'CI/CD Tooling', items: ['Jenkins', 'GitHub Actions', 'GitLab', 'ArgoCD'] },
  { title: 'Monitoring & SRE', items: ['Prometheus', 'Grafana', 'Datadog', 'New Relic'] }
];

const industries = ['Banking & Finance', 'Healthcare', 'Retail', 'Telecom', 'Logistics', 'Public Sector', 'Manufacturing'];

const faqs = [
  {
    q: 'How does Libsys approach a "Well-Architected" cloud migration?',
    a: 'We follow the industry-standard Well-Architected Framework, focusing on five core pillars: Operational Excellence, Security, Reliability, Performance Efficiency, and Cost Optimization. Our migration starts with a deep discovery of your workload dependencies and data requirements. We then design a secure landing zone with automated governance before migrating workloads in governed waves. This ensures your cloud environment is not just "moved," but also "modernized" for long-term scalability and cost-efficiency.'
  },
  {
    q: 'What is the benefit of "Infrastructure as Code" (IaC) for my organization?',
    a: 'IaC transforms infrastructure management into a software engineering discipline. By defining your network, compute, and storage in code (like Terraform or Ansible), you eliminate manual configuration errors and "snowflake" servers. This allows for rapid, repeatable environment provisioning across Dev, Test, and Prod stages. It also provides a clear audit trail of every change made to your infrastructure, improving security compliance and making disaster recovery significantly faster and more reliable.'
  },
  {
    q: 'How do you integrate security into the DevOps lifecycle (DevSecOps)?',
    a: 'We "shift security left" by embedding automated security checkpoints directly into your CI/CD pipelines. This includes static analysis of your code (SAST), secret detection to prevent credential leaks, and dynamic analysis of running environments (DAST). We also implement container image scanning and automated infrastructure compliance checks. By automating these controls, we empower developers to fix security issues early in the build phase, preventing vulnerabilities from ever reaching production.'
  },
  {
    q: 'Can Libsys help manage and optimize our monthly cloud spend (FinOps)?',
    a: 'Absolutely. Cloud waste is a major enterprise challenge. Our FinOps practice combines automated right-sizing tools with governance policies to identify and eliminate underutilized resources. We help your teams implement tagging strategies for cost-attribution, set up automated scheduling for non-production environments, and utilize reserved instances or savings plans for predictable workloads. Our goal is to ensure your cloud investment scales efficiently as your business grows.'
  },
  {
    q: 'What role does SRE (Site Reliability Engineering) play in your managed services?',
    a: 'SRE is the application of software engineering principles to operations. Our SRE teams go beyond traditional support by building automation to handle routine operational tasks (toil). We establish clear Service Level Indicators (SLIs) and Service Level Objectives (SLOs) aligned to your business requirements. When an incident occurs, we use automated incident response and post-mortem analysis to identify root causes and build permanent fixes, continuously improving system reliability and uptime.'
  },
  {
    q: 'Do you support multi-cloud or hybrid-cloud environments?',
    a: 'Yes, most of our enterprise clients operate in hybrid or multi-cloud scenarios. We design abstraction layers and use toolsets like Terraform and Kubernetes that work consistently across different providers. Whether you are bridging on-premise data centers with the public cloud or distributing workloads across AWS and Azure for redundancy, we ensure your networking, security, and deployment pipelines remain unified and governed from a single pane of glass.'
  }
];

const ServiceCloudDevOps = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="bg-white min-h-screen pt-8 [&_.text-h1]:text-h2 [&_.text-h2]:text-h3 [&_.text-body]:text-small [&_.text-small]:text-subtext">
      <Seo
        title="Cloud & DevOps Services"
        description="Libsys Cloud and DevOps consulting — AWS and Azure architecture, CI/CD automation, infrastructure as code, and platform engineering for faster, resilient software delivery."
        path="/services/cloud-devops"
      />
      {/* ══ HERO SECTION ══════════════════════════════════════════════════════ */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 pb-12">
        <div className="rounded-[2.5rem] border border-slate-200 bg-slate-50/70 p-6 lg:p-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }} className="lg:col-span-5">
              <span className="inline-flex items-center gap-2 text-strategic-tagline mb-5">
                Platform Engineering & Ops
              </span>
              <h1 className="text-h2 text-primary mb-5">
                Cloud & DevOps: Accelerating Velocity with Governance
              </h1>
              <p className="text-slate-600 text-body leading-[1.7] font-medium mb-6 max-w-2xl">
                Libsys empowers modern enterprises to ship software with unprecedented speed without compromising on security or stability. We bridge the gap between development and operations by implementing automated CI/CD pipelines, robust Infrastructure as Code (IaC), and resilient SRE frameworks. Whether you are migrating legacy workloads to the cloud or building cloud-native microservices, our delivery ensures your platform is elastic, secure, and cost-optimized. By integrating automated vulnerability scanning and compliance-as-code into every release cycle, we provide a foundation that is as secure as it is fast.
              </p>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-y-3 gap-x-6 mb-8">
                {[
                  'Cloud-Native Transformation',
                  'Zero-Trust DevSecOps Pipelines',
                  'Kubernetes & Platform Engineering',
                  'Infrastructure as Code (Terraform)',
                  'SRE & Performance Optimization',
                  'FinOps & Cloud Cost Governance'
                ].map((item) => (
                  <li key={item} className="text-slate-700 text-subtext font-bold leading-relaxed flex items-start gap-2">
                    <span className="w-2 h-2 rounded-full bg-secondary mt-[7px] flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <div className="flex flex-wrap gap-4">
                <a
                  href="tel:+16307991556"
                  className="inline-flex items-center gap-3 px-8 py-4 bg-primary hover:bg-[#254b85] text-white font-black text-[11px] uppercase tracking-widest rounded-xl transition-all shadow-lg shadow-blue-900/10 active:scale-95 group"
                >
                  Schedule Platform Review
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </a>
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6 }} className="lg:col-span-7 lg:-mt-12">
            <div className="rounded-[2.5rem] overflow-hidden border border-slate-200 shadow-[0_30px_70px_-15px_rgba(35,96,134,0.2)] bg-slate-100 relative group">
              <div className="absolute inset-0 bg-primary/5 group-hover:bg-transparent transition-colors z-10 pointer-events-none" />
              <img src={heroImage} alt="Cloud Infrastructure Simulation" className="w-full h-[400px] lg:h-[600px] object-cover group-hover:scale-105 transition-transform duration-1000" onError={(e) => { e.currentTarget.src = fallbackImage; }} />
            </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ══ CORE CAPABILITIES ════════════════════════════════════════════════ */}
      <section className="bg-slate-50 border-y border-slate-200 py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="mb-14 text-center">
            <span className="text-strategic-tagline block mb-2">Technical Capabilities</span>
            <h2 className="text-h2 text-primary">Advanced Engineering for Resilient Platforms</h2>
            <p className="text-slate-600 text-body max-w-3xl mx-auto mt-4 leading-relaxed">
              We transition organizations from manual infrastructure bottlenecks to automated cloud ecosystems by integrating high-fidelity DevOps practices with secure platform engineering.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {capabilities.map((item) => (
              <div key={item.title} className="bg-white border border-slate-200 rounded-2xl p-8 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                <h3 className="text-[22px] font-black text-primary mb-4 leading-tight">{item.title}</h3>
                <p className="text-slate-600 text-subtext leading-relaxed font-medium">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ SERVICE PORTFOLIO ════════════════════════════════════════════════ */}
      <section className="py-16 max-w-7xl mx-auto px-6 lg:px-12">
        <div className="rounded-[2.5rem] border border-slate-200 bg-slate-50/70 overflow-hidden shadow-sm">
          <div className="grid grid-cols-1 lg:grid-cols-12 items-stretch">
            <div className="lg:col-span-8 p-8 lg:p-12">
              <span className="text-strategic-tagline block mb-3">Service Modules</span>
              <h2 className="text-h2 text-primary mb-6">Unified Platform Operations</h2>
              <p className="text-slate-600 text-body mb-8 max-w-3xl">
                Our modular service approach enables targeted modernization of specific release cycles or infrastructure layers while maintaining a unified security and governance posture.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {modules.map((module) => (
                  <div key={module.title} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                    <h3 className="text-body font-black text-primary mb-4 flex items-center gap-2">
                       {module.title}
                    </h3>
                    <ul className="space-y-3">
                      {module.points.map((point) => (
                        <li key={point} className="text-slate-600 text-subtext font-semibold leading-relaxed flex items-start gap-2.5">
                          <span className="w-1.5 h-1.5 rounded-full bg-slate-300 mt-2 flex-shrink-0" />
                          {point}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
            <div className="lg:col-span-4 border-t lg:border-t-0 lg:border-l border-slate-200 bg-slate-100 relative overflow-hidden">
               <img src={secondaryImage} alt="Platform Delivery Framework" className="h-full w-full object-cover" onError={(e) => { e.currentTarget.src = fallbackImage; }} />
               <div className="absolute inset-0 bg-gradient-to-t from-primary/60 to-transparent flex flex-col justify-end p-8">
                  <p className="text-white text-lg font-black leading-tight italic">"Empowering high-growth enterprises to automate every stage of the software delivery lifecycle."</p>
               </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══ DELIVERY PHASES ══════════════════════════════════════════════════ */}
      <section className="py-16 bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-start">
            <div className="lg:col-span-12 mb-10">
              <span className="text-strategic-tagline block mb-3">Implementation Framework</span>
              <h2 className="text-h2 text-primary mb-6">A Governed Path to Cloud Maturity</h2>
              <p className="text-slate-600 text-body max-w-4xl">
                 Success in the cloud requires more than just migration; it requires a disciplined methodology that integrates security and performance from the first architectural design.
              </p>
            </div>
            <div className="lg:col-span-12">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-12 gap-y-10">
                {executionSteps.map((step, idx) => (
                  <div key={step.phase} className="relative pl-12">
                    <div className="absolute left-0 top-0 text-5xl font-black text-slate-200 leading-none">
                      {(idx + 1).toString().padStart(2, '0')}
                    </div>
                    <div className="relative">
                      <h4 className="text-body font-black text-primary mb-2 uppercase tracking-wide">{step.phase}</h4>
                      <p className="text-slate-600 text-subtext leading-relaxed font-medium">{step.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══ TECH STACK SECTION ═══════════════════════════════════════════════ */}
      <section className="py-16 max-w-7xl mx-auto px-6 lg:px-12">
          <div className="mb-12">
             <span className="text-strategic-tagline block mb-3 text-center lg:text-left">Modern Tech Ecosystem</span>
             <h2 className="text-h2 text-primary mb-6 text-center lg:text-left">Vendor-Agnostic Tooling for Maximum Flexibility</h2>
             <p className="text-slate-600 text-body mb-10 max-w-4xl text-center lg:text-left mx-auto lg:mx-0">
                We leverage a specialized, industry-leading toolset that allows your platform to remain portable, secure, and vendor-neutral across multi-cloud and hybrid environments. Our engineers prioritize tools that offer robust API support and horizontal scalability.
             </p>
             <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                {techStack.map(cat => (
                  <div key={cat.title} className="p-5 rounded-2xl border border-slate-200 bg-slate-50/50 hover:bg-white hover:shadow-lg transition-all">
                     <h5 className="font-black text-primary text-tiny uppercase tracking-widest mb-3">{cat.title}</h5>
                     <div className="flex flex-wrap gap-2">
                        {cat.items.map(i => (
                          <span key={i} className="px-2.5 py-1 bg-white border border-slate-200 rounded-md text-tiny font-bold text-slate-600">{i}</span>
                        ))}
                     </div>
                  </div>
                ))}
             </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-stretch pt-12 border-t border-slate-100">
             <div className="bg-white border border-slate-200 rounded-3xl p-8 shadow-sm h-full">
                <h3 className="text-xl font-black text-primary mb-6">Active Industry Support</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
                   {industries.map(ind => (
                     <div key={ind} className="flex items-center justify-between border-b border-slate-100 pb-3 last:border-b-0 last:pb-0">
                        <span className="text-slate-700 font-bold text-subtext">{ind}</span>
                        <span className="w-1.5 h-1.5 rounded-full bg-secondary" />
                     </div>
                   ))}
                </div>
             </div>
             <div className="flex flex-col gap-6">
                <div className="rounded-3xl border border-slate-200 bg-white p-8 shadow-sm flex-1 flex flex-col justify-center">
                   <p className="text-primary text-xl font-black leading-tight italic">
                      "Helping organizations achieve zero-downtime deployments and 30% reduction in cloud infrastructure spend through disciplined platform engineering."
                   </p>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-3xl p-6 text-center">
                   <p className="text-slate-600 text-tiny font-medium">Standardizing delivery velocity with governed DevOps protocols since 2002.</p>
                </div>
             </div>
          </div>
      </section>

      <FAQAccordion faqs={faqs} />
    </div>
  );
};

export default ServiceCloudDevOps;
